import { useEffect, useState } from "react";
import axios from "axios";
import { Tag, X, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** "Marcar pessoas" — search users and pick up to 20 to tag in a post/photo. */
export default function TagPeoplePicker({ value = [], onChange, excludeUserId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!open) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      axios.get(`${API}/users`, { params: { q, limit: 12 }, signal: ctrl.signal })
        .then((r) => setResults((r.data || []).filter((u) => u.user_id !== excludeUserId)))
        .catch(() => {});
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, open, excludeUserId]);

  const selected = new Set(value.map((u) => u.user_id));
  const toggle = (u) => {
    if (selected.has(u.user_id)) onChange(value.filter((x) => x.user_id !== u.user_id));
    else if (value.length < 20) onChange([...value, { user_id: u.user_id, name: u.name, picture: u.picture }]);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="tag-people-btn">
            <Tag className="w-4 h-4" /> Marcar pessoas{value.length ? ` (${value.length})` : ""}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2" data-testid="tag-people-popover">
          <div className="relative mb-2">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pessoas..." className="pl-8 h-9" data-testid="tag-people-search" autoFocus />
          </div>
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {results.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Nenhum usuário encontrado</div>}
            {results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => toggle(u)}
                data-testid={`tag-people-option-${u.user_id}`}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-accent transition-colors ${selected.has(u.user_id) ? "bg-primary/10" : ""}`}
              >
                <Avatar className="w-7 h-7"><AvatarImage src={u.picture} /><AvatarFallback className="text-[10px]">{u.name?.[0]}</AvatarFallback></Avatar>
                <span className="text-sm truncate flex-1">{u.name}</span>
                {selected.has(u.user_id) && <span className="text-[10px] font-bold text-primary">✓</span>}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {value.map((u) => (
        <span key={u.user_id} className="inline-flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium" data-testid={`tag-chip-${u.user_id}`}>
          <Avatar className="w-4 h-4"><AvatarImage src={u.picture} /><AvatarFallback className="text-[8px]">{u.name?.[0]}</AvatarFallback></Avatar>
          {u.name}
          <button type="button" onClick={() => toggle(u)} className="hover:text-destructive" aria-label="Remover"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
  );
}
